import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { IconInfo, IconX } from './Icons';
import {
  readTraderPsychologyTipEnabled,
  writeTraderPsychologyTipEnabled,
} from '../utils/traderPsychologyTipPreference';

const CSV_PATH = `${process.env.PUBLIC_URL || ''}/trader-psychology-tips.csv`;
const ROTATE_MS = 28000;

const TIP_BANNER_HEIGHT_VAR = '--risklo-tip-banner-height';

function setTipBannerHeightCss(px) {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(TIP_BANNER_HEIGHT_VAR, `${px}px`);
}

function parseTipsCsv(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return [];
  const skipHeader = lines[0].toLowerCase() === 'tip';
  return skipHeader ? lines.slice(1) : lines;
}

/**
 * Full-width banner with rotating tips loaded from /trader-psychology-tips.csv (public/).
 * Preference: localStorage + Account page toggle; X sets disabled until re-enabled in Account.
 */
export default function TraderPsychologyTipBanner() {
  const { user } = useAuth();
  const email = user?.email ? String(user.email).trim() : null;

  const [tips, setTips] = useState([]);
  const [index, setIndex] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const bannerRef = useRef(null);

  const refreshEnabled = useCallback(() => {
    setEnabled(readTraderPsychologyTipEnabled(email));
  }, [email]);

  useLayoutEffect(() => {
    refreshEnabled();
  }, [refreshEnabled]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key && e.key.includes('trader_psychology_tip_enabled')) refreshEnabled();
    };
    const onCustom = () => refreshEnabled();
    window.addEventListener('storage', onStorage);
    window.addEventListener('risklo-trader-psychology-tip-preference', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('risklo-trader-psychology-tip-preference', onCustom);
    };
  }, [refreshEnabled]);

  useEffect(() => {
    let cancelled = false;
    fetch(CSV_PATH)
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        const list = parseTipsCsv(text);
        if (!list.length) {
          setLoadFailed(true);
          return;
        }
        setTips(list);
        setIndex(Math.floor(Math.random() * list.length));
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (tips.length < 2) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % tips.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [tips.length]);

  /**
   * Offset fixed chrome (PageHeader, floating hamburger) by banner height only while the banner
   * intersects the viewport. When the user scrolls the strip away, offset goes to 0 so nothing
   * leaves a gap at the top.
   */
  useLayoutEffect(() => {
    const el = bannerRef.current;
    if (!el) {
      setTipBannerHeightCss(0);
      return undefined;
    }

    let tipStripVisible = true;

    const apply = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      setTipBannerHeightCss(tipStripVisible && h > 0 ? h : 0);
    };

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        tipStripVisible = entry.isIntersecting;
        apply();
      },
      { threshold: 0, root: null, rootMargin: '0px' }
    );
    io.observe(el);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', apply);
    apply();

    return () => {
      io.disconnect();
      if (ro) ro.disconnect();
      window.removeEventListener('resize', apply);
      setTipBannerHeightCss(0);
    };
  }, [enabled, loadFailed, tips.length]);

  const handleDismiss = () => {
    writeTraderPsychologyTipEnabled(email, false);
    setEnabled(false);
  };

  if (!enabled || loadFailed || !tips.length) return null;

  const tip = tips[index] || '';

  return (
    <div
      ref={bannerRef}
      className="trader-psychology-banner"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="trader-psychology-banner-inner">
        <span className="trader-psychology-banner-icon" aria-hidden="true">
          <IconInfo size={20} color="currentColor" />
        </span>
        <p className="trader-psychology-banner-text">
          <span className="trader-psychology-banner-label">Trader Psychology Tip:</span>{' '}
          {tip}
        </p>
      </div>
      <button
        type="button"
        className="trader-psychology-banner-close"
        onClick={handleDismiss}
        aria-label="Hide trader psychology tips"
      >
        <IconX size={18} color="currentColor" />
      </button>
    </div>
  );
}
